import { useIsMobile } from '@src/hooks/useIsMobile';
import { EButtonIntents } from '@src/typings';
import { DatePickerProps, Input } from 'antd';
import {
    CalendarPicker as CalendarPickerAntMobile,
    CalendarPickerProps,
    ConfigProvider,
} from 'antd-mobile';
import 'antd-mobile/es/global';
import enUS from 'antd-mobile/es/locales/en-US';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useState } from 'react';
import { StyledFormItem } from '../FileUpload/styles';
import { withTheme } from '../ThemeProvider/withTheme';
import {
    DatePickerDropdownStyles,
    StyledApplyButton,
    StyledCalendarIcon,
    StyledCancelButton,
    StyledFooterRow,
    StyledIconButton,
    StyledSingleDatePicker,
} from './styles';
import { SingleCalenderProps } from './types';

const SingleCalenderComponent: FC<SingleCalenderProps> = ({
    value,
    defaultValue,
    placeholder,
    name,
    placement = 'bottomLeft',
    allowClear = true,
    disabled = false,
    format = 'YYYY-MM-DD',
    disabledDate,
    showToday = false,
    status,
    layoutLabel = 'vertical',
    size = 'middle',
    renderFooter,
    className,
    style,
    label,
    ref,
    onChange,
    formItemProps,
    inputMobileProps,
    required,
    ...props
}) => {
    const isMobile = useIsMobile();
    //states
    const [selectionMobile, setSelectionMobile] = useState<Date>(
        value instanceof Date ? value : new Date()
    );
    const [tempSelectionMobile, setTempSelectionMobile] =
        useState(selectionMobile);

    const [selectionDesktop, setSelectionDesktop] = useState<Dayjs>(() =>
        dayjs.isDayjs(value) ? value : dayjs(value || new Date())
    );
    const [tempSelectionDesktop, setTempSelectionDesktop] =
        useState(selectionDesktop);

    const [openDesktop, setOpenDesktop] = useState(false);
    const [openMobile, setMobileOpen] = useState(false);

    // Desktop handlers
    const handleCalendarChangeDesktop = (date: unknown) =>
        setTempSelectionDesktop(date as Dayjs);

    const handleChangeDesktop: DatePickerProps['onChange'] = (
        date,
        _dateString
    ) => {
        const dayjsDate = date as Dayjs;
        if (dayjs.isDayjs(dayjsDate)) {
            setTempSelectionDesktop(dayjsDate);
        }
    };

    const handelCancelButtonDesktop = () => {
        setTempSelectionDesktop(selectionDesktop);
        setOpenDesktop(false);
    };

    const handelApplyButtonDesktop = () => {
        setSelectionDesktop(tempSelectionDesktop);
        onChange?.(tempSelectionDesktop);
        setOpenDesktop(false);
    };

    // Mobile handlers
    const handleChangeCalendarMobile = (val: Date | null) => {
        if (dayjs.isDayjs(val)) {
            setTempSelectionMobile(val.toDate());
        }
    };

    const handleApplyButtonMobile = () => {
        setSelectionMobile(tempSelectionMobile);
        onChange?.(tempSelectionMobile);
        setMobileOpen(false);
    };

    const handleCancelButtonMobile = () => {
        setTempSelectionMobile(selectionMobile);
        setMobileOpen(false);
    };

    const Footer = (
        <StyledFooterRow>
            <StyledCancelButton
                intent={EButtonIntents.LightLinear}
                size="sm"
                onClick={handelCancelButtonDesktop}
                className="!rounded"
            >
                Cancel
            </StyledCancelButton>
            <StyledApplyButton
                intent={EButtonIntents.Filled}
                size="sm"
                onClick={handelApplyButtonDesktop}
                className="!rounded"
            >
                Apply
            </StyledApplyButton>
        </StyledFooterRow>
    );

    const CalenderMobile = (
        <StyledFormItem
            name={name}
            layout={layoutLabel}
            label={label}
            required={required}
        >
            <Input
                disabled={disabled}
                placeholder={placeholder}
                onClick={() => setMobileOpen(true)}
                suffix={
                    <StyledIconButton onClick={() => setMobileOpen(true)}>
                        <StyledCalendarIcon />
                    </StyledIconButton>
                }
                value={dayjs(tempSelectionMobile).format(format)}
                allowClear={allowClear}
                {...inputMobileProps}
            />
            <ConfigProvider locale={enUS}>
                <CalendarPickerAntMobile
                    confirmText="Apply"
                    allowClear={allowClear}
                    {...(props as any as CalendarPickerProps)}
                    visible={openMobile}
                    selectionMode="single"
                    className={className}
                    style={style}
                    value={tempSelectionMobile}
                    defaultValue={selectionMobile}
                    onMaskClick={handleCancelButtonMobile}
                    onClose={handleCancelButtonMobile}
                    onConfirm={handleApplyButtonMobile}
                    onChange={handleChangeCalendarMobile}
                />
            </ConfigProvider>
        </StyledFormItem>
    );

    const CalendarDesktop = (
        <StyledFormItem
            name={name}
            layout={layoutLabel}
            label={label}
            required={required}
        >
            <DatePickerDropdownStyles />
            <StyledSingleDatePicker
                {...props}
                disabled={disabled}
                allowClear={allowClear}
                showToday={showToday}
                status={status}
                size={size}
                format={format}
                disabledDate={disabledDate}
                className={className}
                style={style}
                placement={placement}
                open={openDesktop}
                onOpenChange={(open) => {
                    if (open) {
                        setOpenDesktop(true);
                    }
                }}
                defaultValue={defaultValue}
                value={tempSelectionDesktop}
                onCalendarChange={handleCalendarChangeDesktop}
                // @ts-expect-error: Type mismatch due to library typing issue FIXME: should fix type error later
                onChange={handleChangeDesktop}
                suffixIcon={
                    <StyledIconButton onClick={() => setOpenDesktop(true)}>
                        <StyledCalendarIcon />
                    </StyledIconButton>
                }
                dropdownClassName="my-range-picker-dropdown"
                panelRender={(menu) => (
                    <div className="my-range-picker-dropdown">
                        {menu}
                        {renderFooter ? renderFooter() : Footer}
                    </div>
                )}
            />
        </StyledFormItem>
    );

    return isMobile ? CalenderMobile : CalendarDesktop;
};

export const SingleCalender = withTheme(SingleCalenderComponent);
